import { useState, useRef, useCallback } from "react";
import ConfirmDialog from "@/shared/ui/ConfirmDialog";
import {
  ConfirmDialogContext,
  type ConfirmDialogOptions,
  type ConfirmFunction,
} from "@/providers/confirmDialogContext";

const DEFAULTS = {
  title: "Are you sure?",
  message: "Are you sure you want to proceed?",
  confirmLabel: "Yes",
  cancelLabel: "No",
  destructive: false,
} as const;

export default function ConfirmDialogProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Required<ConfirmDialogOptions>>(DEFAULTS);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFunction>((opts) => {
    setOptions({
      title: opts?.title ?? DEFAULTS.title,
      message: opts?.message ?? DEFAULTS.message,
      confirmLabel: opts?.confirmLabel ?? DEFAULTS.confirmLabel,
      cancelLabel: opts?.cancelLabel ?? DEFAULTS.cancelLabel,
      destructive: opts?.destructive ?? DEFAULTS.destructive,
    });
    setOpen(true);

    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setOpen(false);
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setOpen(false);
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={open}
        title={options.title}
        message={options.message}
        confirmLabel={options.confirmLabel}
        cancelLabel={options.cancelLabel}
        destructive={options.destructive}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmDialogContext.Provider>
  );
}

